/**
 * Tope de espera para una promesa de red.
 *
 * ── Por qué hace falta ─────────────────────────────────────────────────────
 *
 * 🔴 Apollo **no tiene timeout propio** y las queries no pasan por el techo de
 * `OfflineQueueLink` —ese es solo para las mutations replay-seguras, 25 s—. Con
 * una raya de cobertura `navigator.onLine` sigue diciendo `true`, la petición
 * sale y **ni responde ni falla**: todo respaldo escrito en el `catch` queda
 * inalcanzable y quien espera se queda esperando.
 *
 * Medido el 2026-09-08 con el backend en el portátil y Atlas por el hotspot:
 * consultas de 64 ms a 4.400 ms, seis «Server selection timeout» y seis
 * `ECONNRESET` en una mañana; el driver de Mongo aguanta 30 s antes de rendirse.
 *
 * ── Lo que NO hace, a propósito ────────────────────────────────────────────
 *
 * No cancela la petición (`AbortController`). Abortarla la convertiría en un
 * error de red que `RetryLink` podría reintentar, y su resultado tardío es
 * inocuo: se deja morir por su cuenta. Decisión heredada de
 * `docs/superpowers/specs/2026-09-08-pk-malla-primero-design.md` § 4.
 *
 * Estaba privado dentro de `useGpsToPK`; se extrae al aparecer el segundo
 * llamador (el botón de autolocalizar PK).
 */

/** Se agotó el plazo: esperado con poca cobertura, no es un incidente. */
export class NetworkTimeoutError extends Error {
  constructor(ms: number) {
    super(`la consulta no contestó en ${ms} ms`);
    this.name = 'NetworkTimeoutError';
  }
}

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new NetworkTimeoutError(ms)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export default withTimeout;
